import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart2, Users, FileText, Calendar, Zap, Inbox, HardDrive, LogIn } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_DEFS = [
  { key: 'users_count', label: 'Users', icon: Users, color: '#3182CE' },
  { key: 'crm_records_count', label: 'CRM Records', icon: FileText, color: '#38A169' },
  { key: 'appointments_count', label: 'Appointments', icon: Calendar, color: '#D69E2E' },
  { key: 'automation_runs_count', label: 'Automations', icon: Zap, color: '#9F7AEA' },
  { key: 'forms_submitted_count', label: 'Forms', icon: Inbox, color: '#ED8936' },
  { key: 'storage_mb', label: 'Storage (MB)', icon: HardDrive, color: '#E53E3E' },
  { key: 'website_pages_count', label: 'Pages', icon: FileText, color: '#4FD1C5' },
  { key: 'logins_count', label: 'Logins', icon: LogIn, color: '#FC8181' },
];

export default function SaaSUsage() {
  const [metrics, setMetrics] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMetrics(); }, []);

  const fetchMetrics = async () => {
    try {
      const res = await axios.get(`${API}/saas/usage`);
      setMetrics(res.data);
    } catch { toast.error('Failed to load usage metrics'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async (tenantId) => {
    try {
      setSelectedTenant(tenantId);
      const res = await axios.get(`${API}/saas/usage/${tenantId}/history?days=30`);
      setHistory(res.data);
    } catch { toast.error('Failed to load usage history'); }
  };

  const totals = METRIC_DEFS.reduce((acc, m) => {
    acc[m.key] = metrics.reduce((sum, t) => sum + (t[m.key] || 0), 0);
    return acc;
  }, {});

  if (loading) return <div className="saas-loading">Loading usage metrics...</div>;

  return (
    <div className="saas-page" data-testid="saas-usage">
      <div className="saas-page-header">
        <div>
          <h2>Usage Metrics</h2>
          <p>{metrics.length} tenants reporting</p>
        </div>
      </div>

      <div className="saas-kpi-grid small">
        {METRIC_DEFS.map(m => {
          const Icon = m.icon;
          return (
            <div className="saas-kpi-card compact" key={m.key}>
              <div className="kpi-icon" style={{ background: `${m.color}20`, color: m.color }}><Icon size={20} /></div>
              <div className="kpi-content">
                <span className="kpi-label">{m.label}</span>
                <span className="kpi-value">{m.key === 'storage_mb' ? `${totals[m.key]?.toFixed(1)}` : totals[m.key]}</span>
              </div>
            </div>
          );
        })}
      </div>

      {history.length > 0 && selectedTenant && (
        <div className="saas-widget" style={{ marginBottom: 24 }}>
          <div className="widget-header">
            <h3>Usage Trend - {metrics.find(m => m.tenant_id === selectedTenant)?.tenant_name || 'Tenant'}</h3>
            <button className="widget-refresh" onClick={() => { setHistory([]); setSelectedTenant(null); }}>Close</button>
          </div>
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3d" />
                <XAxis dataKey="metric_date" stroke="#888" tick={{ fontSize: 11 }} />
                <YAxis stroke="#888" />
                <Tooltip contentStyle={{ background: '#232326', border: '1px solid #3a3a3d', borderRadius: 8, color: '#e5e5e5' }} />
                <Line type="monotone" dataKey="logins_count" stroke="#3182CE" name="Logins" strokeWidth={2} />
                <Line type="monotone" dataKey="crm_records_count" stroke="#38A169" name="CRM Records" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="saas-table-container">
        <table className="saas-table" data-testid="usage-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Users</th>
              <th>CRM Records</th>
              <th>Appointments</th>
              <th>Automations</th>
              <th>Forms</th>
              <th>Storage</th>
              <th>Pages</th>
              <th>Logins</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => (
              <tr key={m.tenant_id} className="clickable-row" onClick={() => fetchHistory(m.tenant_id)}>
                <td>
                  <div className="tenant-cell">
                    <div className="tenant-avatar">{(m.tenant_name || 'U').charAt(0)}</div>
                    <div className="tenant-info">
                      <strong>{m.tenant_name || 'Unknown'}</strong>
                      <span>{m.template_slug}</span>
                    </div>
                  </div>
                </td>
                <td>{m.users_count || 0}</td>
                <td>{m.crm_records_count || 0}</td>
                <td>{m.appointments_count || 0}</td>
                <td>{m.automation_runs_count || 0}</td>
                <td>{m.forms_submitted_count || 0}</td>
                <td>{(m.storage_mb || 0).toFixed(1)} MB</td>
                <td>{m.website_pages_count || 0}</td>
                <td>{m.logins_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {metrics.length === 0 && (
          <div className="empty-state"><p>No usage data recorded yet</p></div>
        )}
      </div>
    </div>
  );
}
